import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Gift, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * 18-B2 item 6: admin — pilot-area config (the kill switch), offer CRUD and
 * the redemptions-by-offer/month measurement table with CSV export.
 */
export function PerksPanel() {
  const [data, setData] = useState({ areas: [], offers: [] });
  const [redemptions, setRedemptions] = useState(null);
  const [areasInput, setAreasInput] = useState("");
  const [form, setForm] = useState({ partner_name: "", area: "", offer_text: "", min_tier: "silver", monthly_cap_per_user: 1 });

  const load = () => {
    api.adminPerks().then((d) => { setData(d); setAreasInput((d.areas || []).join(", ")); }).catch(() => {});
    api.perkRedemptions().then(setRedemptions).catch(() => {});
  };
  useEffect(load, []);

  const saveAreas = async () => {
    const areas = areasInput.split(",").map((a) => a.trim()).filter(Boolean);
    await api.setPerksConfig(areas);
    toast.success(areas.length ? `Pilot live in: ${areas.join(", ")}` : "Perks hidden everywhere (kill switch)");
    load();
  };

  const create = async () => {
    try {
      await api.createPerk({ ...form, monthly_cap_per_user: Number(form.monthly_cap_per_user) || 1 });
      setForm({ partner_name: "", area: "", offer_text: "", min_tier: "silver", monthly_cap_per_user: 1 });
      toast.success("Offer created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const toggle = async (o) => {
    await api.updatePerk(o.id, { active: !o.active });
    load();
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete the ${o.partner_name} offer? Redemption data is kept.`)) return;
    await api.deletePerk(o.id);
    toast("Offer deleted — redemption data retained");
    load();
  };

  const exportCsv = async () => {
    const blob = await api.perkRedemptionsCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "perk-redemptions.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 mt-8" data-testid="admin-perks-panel">
      <h2 className="font-display text-2xl text-forest tracking-tight inline-flex items-center gap-2">
        <Gift className="w-5 h-5 text-terracotta" /> Perks pilot
      </h2>
      <p className="text-xs text-neutral-500 mt-1">
        A retention experiment, not a currency. Emptying the areas list hides all perks UI everywhere — data is retained.
      </p>

      <div className="flex gap-2 mt-4 flex-wrap items-center">
        <Input data-testid="perks-areas-input" value={areasInput} onChange={(e) => setAreasInput(e.target.value)}
               placeholder="Pilot areas, comma-separated (empty = off)" className="max-w-md h-9 text-sm" />
        <Button data-testid="perks-areas-save" onClick={saveAreas} variant="outline" className="rounded-full h-9 px-4 text-xs">Save areas</Button>
        <Badge className={`rounded-full text-[10px] border-none ${data.areas.length ? "bg-forest text-white" : "bg-neutral-200 text-neutral-600"}`}>
          {data.areas.length ? `Live in ${data.areas.length} area${data.areas.length > 1 ? "s" : ""}` : "Off"}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">New offer</div>
          <div className="space-y-2 mt-2">
            <Input data-testid="perk-partner-input" placeholder="Partner (e.g. Corner Bakery)" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="h-9 text-sm" />
            <Input data-testid="perk-area-input" placeholder="Area (must be a pilot area)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="h-9 text-sm" />
            <Input data-testid="perk-text-input" placeholder="Offer text (e.g. a coffee on us)" value={form.offer_text} onChange={(e) => setForm({ ...form, offer_text: e.target.value })} className="h-9 text-sm" />
            <div className="flex gap-2">
              <select data-testid="perk-tier-select" value={form.min_tier} onChange={(e) => setForm({ ...form, min_tier: e.target.value })} className="h-9 rounded-md border border-neutral-200 bg-white px-2 text-sm flex-1">
                <option value="bronze">Bronze+</option>
                <option value="silver">Silver+ (default)</option>
                <option value="gold">Gold+</option>
                <option value="platinum">Platinum</option>
              </select>
              <Input data-testid="perk-cap-input" type="number" min={1} max={31} value={form.monthly_cap_per_user} onChange={(e) => setForm({ ...form, monthly_cap_per_user: e.target.value })} className="h-9 text-sm w-24" title="Monthly cap per user" />
            </div>
            <Button data-testid="perk-create-btn" onClick={create} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-5 text-xs">Create offer</Button>
          </div>
          <div className="space-y-2 mt-4" data-testid="perk-offers-list">
            {data.offers.map((o) => (
              <div key={o.id} className="border border-neutral-200 rounded-xl px-3 py-2 flex items-center gap-2 text-sm" data-testid={`perk-row-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <b className="text-forest">{o.partner_name}</b> · {o.offer_text}
                  <div className="text-xs text-neutral-400">{o.area} · {o.min_tier}+ · cap {o.monthly_cap_per_user}/mo</div>
                </div>
                <button data-testid={`perk-toggle-${o.id}`} onClick={() => toggle(o)} className={`text-xs px-2.5 py-1 rounded-full border ${o.active ? "border-forest/30 text-forest" : "border-neutral-200 text-neutral-400"}`}>
                  {o.active ? "Active" : "Inactive"}
                </button>
                <button data-testid={`perk-delete-${o.id}`} onClick={() => remove(o)} className="text-xs text-red-500 hover:underline">Delete</button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Redemptions by offer / month</div>
            <Button data-testid="perk-csv-btn" onClick={exportCsv} variant="outline" className="rounded-full h-8 px-3 text-xs">
              <Download className="w-3 h-3 mr-1.5" /> CSV
            </Button>
          </div>
          <div className="mt-2" data-testid="perk-redemptions-table">
            {(!redemptions || redemptions.rows.length === 0) && (
              <div className="text-sm text-neutral-400 py-4">No redemptions yet.</div>
            )}
            {redemptions?.rows.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-neutral-100 py-2 text-sm">
                <div className="min-w-0">
                  <b className="text-forest">{r.partner_name}</b>
                  <span className="text-neutral-500"> · {r.month}</span>
                </div>
                <div className="text-neutral-600 shrink-0">{r.redemptions} ({r.unique_users} people)</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
